import type { Application } from '@labeling/globals';
import {
  Avatar,
  Button,
  Combobox,
  InlineAlert,
  Tag,
  TextField,
} from '@lego/connect-components-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { KnownOperator } from './types';
import { initials, operatorDisplayName } from './utils';

const UnrestrictedOperatorRow = ({
  operator,
  groupSuggestions,
  isBusy,
  onRestrictOperator,
}: {
  operator: KnownOperator;
  groupSuggestions: string[];
  isBusy: boolean;
  onRestrictOperator: (opId: number, group: string) => Promise<boolean>;
}) => {
  const { t } = useTranslation();
  const [groupName, setGroupName] = useState('');
  const displayName = operatorDisplayName(operator);
  const restrict = async () => {
    if (await onRestrictOperator(operator.id, groupName)) setGroupName('');
  };

  return (
    <div className="flex items-center gap-200 px-200 py-150">
      <Avatar type="initials" name={displayName} initials={initials(displayName)} size="medium" />
      <div className="flex-1 min-w-0">
        <div className="text-body-xs font-semibold flex items-center gap-100">
          <span className="truncate">{displayName}</span>
          {!operator.oid && (
            <Tag variant="informative" size="small">
              {t('operatorAccessPage.display.invitedTag')}
            </Tag>
          )}
        </div>
        {operator.email && operator.displayName && (
          <div className="text-body-xs text-core-slate-900 truncate">{operator.email}</div>
        )}
      </div>
      <div className="w-[160px]">
        {groupSuggestions.length > 0 ? (
          <Combobox
            fullWidth
            value={groupName ? [groupName] : []}
            onChange={(_e, items) => setGroupName(items[0]?.value ?? '')}
            placeholder={t('operatorAccessPage.display.pickGroup')}
            size="medium"
          >
            {[
              ...groupSuggestions,
              ...(groupName && !groupSuggestions.includes(groupName) ? [groupName] : []),
            ].map((group) => (
              <Combobox.Option key={group} value={group}>
                {group}
              </Combobox.Option>
            ))}
          </Combobox>
        ) : (
          <TextField
            size="medium"
            value={groupName}
            onChange={(_e, next) => setGroupName(next)}
            placeholder={t('operatorAccessPage.display.newGroupPlaceholder')}
          />
        )}
      </div>
      <Button
        variant="primary"
        size="medium"
        onClick={() => void restrict()}
        disabled={isBusy || !groupName.trim()}
      >
        {t('operatorAccessPage.action.restrict')}
      </Button>
    </div>
  );
};

export const UnrestrictedGroupPanel = ({
  application,
  unrestricted,
  groupSuggestions,
  isBusy,
  onRestrictOperator,
}: {
  application: Application;
  unrestricted: KnownOperator[];
  groupSuggestions: string[];
  isBusy: boolean;
  onRestrictOperator: (opId: number, group: string) => Promise<boolean>;
}) => {
  const { t } = useTranslation();
  return (
    <>
      <div className="px-300 py-200 border-b border-core-slate-100 bg-core-blue-50/60 flex items-center justify-between">
        <div>
          <div className="text-body-xs text-core-slate-900 font-medium">{application}</div>
          <div className="font-bold text-core-slate-1300 text-body-md italic flex items-center gap-100">
            {t('operatorAccessPage.display.unrestrictedGroup')}
            <Tag
              variant="neutral"
              size="small"
              aria-label={t('operatorAccessPage.display.memberCountTooltip', {
                count: unrestricted.length,
              })}
            >
              {String(unrestricted.length)}
            </Tag>
          </div>
        </div>
      </div>

      <div className="p-300 space-y-300 overflow-y-auto flex-1">
        <InlineAlert variant="informative">
          <InlineAlert.Content>
            {t('operatorAccessPage.display.unrestrictedExplanation')}
          </InlineAlert.Content>
        </InlineAlert>

        {unrestricted.length === 0 ? (
          <div className="border border-dashed border-core-slate-100 rounded-lg p-300 text-center text-body-xs text-core-slate-900">
            {t('operatorAccessPage.display.noUnrestricted')}
          </div>
        ) : (
          <div className="border border-core-slate-100 rounded-lg divide-y divide-core-slate-100">
            {unrestricted.map((op) => (
              <UnrestrictedOperatorRow
                key={op.id}
                operator={op}
                groupSuggestions={groupSuggestions}
                isBusy={isBusy}
                onRestrictOperator={onRestrictOperator}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
};
